import React, { useMemo, useState } from 'react';
import "./MessageDetailsEdit.scss";
import { Checkbox, Radio, Select, Switch, TextField } from '@livingdesign/react';
import EditFormActionsFooter from './EditFormActionsFooter';
import { useMessageDetailsForm } from '../../../../../contexts/MessageDetailsForm';
import { useAppContext } from '../../../../../contexts/Core';
import FromFieldSelect from './FormComponents/FromFieldSelect';
import CommonFieldSelect from './FormComponents/CommonFieldSelect';
import { CAMPAIGN_FIELDS, CAMPAIGN_FIELDS_V2, CAMPAIGN_TYPES, NOTIFICATION_CHANNELS, SNIPPET_MAX_CHAR, SUBJECT_MAX_CHAR } from '../../../../../consts/Campaign';
import { CONFIGURATIONS, EXTERNAL_USER_OPTIONS_CONFIGS, NOTIFICATION } from '../../../../../consts/DBFields';
import { useCampaignContext } from '../../../../../contexts/Campaign';
import { hasRecipientFields } from '../../../../../utils/campaignEdit';
import { isSystemNotificationsCampaign } from '../../../../../utils/campaign';
import InputErrorText from '../../../common/Error/InputErrorText';
import { STEPS_V2 } from '../../../../../consts/CampaignEdit';

export const QA_ID_PREFIX = "EditLayout-MessageDetailsEdit";

function MessageDetailsEdit({
  stepIndex,
}) {

  const {
    sendersList,
  } = useAppContext();

  const {
    handleCampaignTypeSelect,
    setNeedToSaveTemplate,
  } = useCampaignContext();

  const {
    control,
    errors,
    Controller,
    formValues,
    continueButtonLoading,
    handleContinue,
    selectedCampaign,
    setAlertModel,
    alertModel,
    notificationIndex,
    handlePrevious,
    saveDraft,
    handleAutoSave,
    // handleCampaignTypeSelectV2,
  } = useMessageDetailsForm();

  const [loading, setLoading] = useState(false);

  const handleCampaignTypeChange = async (field) => {
    setLoading(true);
    const value = !field.value;
    field.onChange({target: { value }});
    const campaignTypeValue = value ? CAMPAIGN_TYPES.personalized.value : CAMPAIGN_TYPES.general.value;
    await handleCampaignTypeSelect(campaignTypeValue);
    setLoading(false);
  }

  const disableInputs = useMemo(() => {
    return continueButtonLoading || loading;
  }, [continueButtonLoading, loading]);

  const showExternalEmailCheckOptions = useMemo(() => {
    if(formValues.channel === "mail" && formValues.campaignType === CAMPAIGN_TYPES.personalized.value) {
      return true;
    }
    return false;
  }, [formValues.channel, formValues.campaignType]);

  const isTemplateSaveRequired = (channel, campaign, notificationIndex) => {
    if(channel === NOTIFICATION_CHANNELS.mail.value && STEPS_V2.CONTENT.isComplete(campaign, false, notificationIndex)) {
      return true;
    }
    return false;
  }

  return (
    <div data-testid={`${QA_ID_PREFIX}-container`}>
      <div className='message-details-edit-title' data-testid={`${QA_ID_PREFIX}-title-container`}>
        <h2 data-testid={`${QA_ID_PREFIX}-title`}>Add your message details</h2>
        <p data-testid={`${QA_ID_PREFIX}-description`}>Include essential fields and add attachments. Discover how to track message performance to measure engagement and effectiveness.</p>
      </div>
      <div className='message-details-edit-form' data-testid={`${QA_ID_PREFIX}-form-container`}>
        <div className='typo-1' data-testid={`${QA_ID_PREFIX}-required-field-label`}>*Required field</div>
        <div data-testid={`${QA_ID_PREFIX}-form-fields`}>
          <Controller
            name={"from"}
            control={control}
            render={({ field }) => (
              <FromFieldSelect
                {...field}
                onBlur={(e) => {
                  console.log(`from field onBlur, value=${field.value}`);
                  handleAutoSave();
                }}
                name={"from"}
                label="From*"
                style={{ paddingTop: "8px" }}
                error={errors.from ? errors.from.message : null}
                sendersList={sendersList}
                disabled={disableInputs}
                data-testid={`${QA_ID_PREFIX}-from-field`}
              />
            )}
          />
          <Controller
            name={"priority"}
            control={control}
            render={({ field }) => (
              <CommonFieldSelect
                name={"priority"}
                label={"Priority*"}
                style={{
                  paddingTop: "24px"
                }}
                onBlur={(e) => {
                  console.log(`priority field onBlur, value=${field.value}`);
                  handleAutoSave();
                }}
                error={errors.priority ? errors.priority.message : null}
                options={CAMPAIGN_FIELDS_V2[NOTIFICATION.CONFIGURATIONS][CONFIGURATIONS.PRIORITY].options}
                defaultValue={CAMPAIGN_FIELDS_V2[NOTIFICATION.CONFIGURATIONS][CONFIGURATIONS.PRIORITY].defaultValue}
                value={field.value}
                handleChange={field.onChange}
                disabled={disableInputs}
                data-testid={`${QA_ID_PREFIX}-priority-field`}
              />
            )}
          />
          <Controller
            name={"subject"}
            control={control}
            render={({ field }) => (
              <TextField
                name={"subject"}
                label={"Subject*"}
                UNSAFE_style={{
                  paddingTop: "24px"
                }}
                error={errors.subject ? errors.subject.message : null}
                {...field}
                onBlur={(e) => {
                  field.onChange(e.target.value.trim());
                  handleAutoSave();
                }}
                disabled={disableInputs}
                data-testid={`${QA_ID_PREFIX}-subject-field`}
              />
            )}
          />
          <Controller
            name={"snippet"}
            control={control}
            render={({ field }) => (
              <TextField
                name={"snippet"}
                label={"Snippet text"}
                UNSAFE_style={{
                  paddingTop: "24px"
                }}
                error={errors.snippet ? errors.snippet.message : null}
                {...field}
                onBlur={(e) => {
                  field.onChange(e.target.value.trim());
                  handleAutoSave();
                }}
                disabled={disableInputs}
                data-testid={`${QA_ID_PREFIX}-snippet-field`}
              />
            )}
          />
          <div className='typo-1' data-testid={`${QA_ID_PREFIX}-snippet-description`}>This is the first line your recipients will see after the subject line</div>
          
          <div className='message-personalisation-edit' data-testid={`${QA_ID_PREFIX}-personalisation-container`}>
            <h3 data-testid={`${QA_ID_PREFIX}-personalisation-title`}>Do you want to personalise your message?</h3>
            <p data-testid={`${QA_ID_PREFIX}-personalisation-description`}>Enable the toggle below to send a personalised message to your recipients.</p>
            <Controller
              name={"personaliseMessage"}
              control={control}
              render={({ field }) => {
                return(                
                <Switch
                  name={"personaliseMessage"}
                  label="Personalise your message"
                  onClick={ async () => {
                    // If the campaign has recipient fields, show an alert modal before changing the type
                    if(hasRecipientFields(selectedCampaign, notificationIndex)) {
                      setAlertModel({
                      show: true,
                      handleClose: () => setAlertModel({ ...alertModel, show: false }),
                      title: "Are you sure you want to change this campaign type?",
                      message: "This will erase all current recipient settings.",
                      confirmButtonText: "Yes",
                      cancelButtonText: "Never mind",
                      handleConfirm: async () => {
                        handleCampaignTypeChange(field);
                      },
                    })
                    } else {
                      handleCampaignTypeChange(field);
                    }
                  }}
                  isOn={field.value}
                  disabled={disableInputs || isSystemNotificationsCampaign(selectedCampaign)}
                  data-testid={`${QA_ID_PREFIX}-personalise-switch`}
                />
              )}}
            />
          </div>

          {showExternalEmailCheckOptions &&
            <div className='has-external-users-input' data-testid={`${QA_ID_PREFIX}-external-users-container`}>
              <h3 data-testid={`${QA_ID_PREFIX}-external-users-title`}>Is the message targeted to external users?*</h3>
              <p data-testid={`${QA_ID_PREFIX}-external-users-description`}>Select an option to confirm whether this message is targeted to external users</p>
              <Controller
                name="hasExternalUsers"
                control={control}
                render={({ field }) => (
                  <div
                    name="hasExternalUsers"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                    data-testid={`${QA_ID_PREFIX}-external-users-options`}
                  >
                    <Radio
                      label="Yes"
                      value={"true"}
                      name="hasExternalUsers"
                      checked={field.value === "true"}
                      onChange={(e) => {
                        field.onChange(e);
                        handleAutoSave();
                      }}
                      disabled={disableInputs}
                      data-testid={`${QA_ID_PREFIX}-external-users-yes`}
                    />
                    <Radio
                      name='hasExternalUsers'
                      label="No"
                      value={"false"}
                      checked={field.value === "false"}
                      onChange={(e) => {
                        field.onChange(e);
                        handleAutoSave();
                      }}
                      disabled={disableInputs}
                      data-testid={`${QA_ID_PREFIX}-external-users-no`}
                    />
                  </div>
                )}
              />
              {(errors.hasExternalUsers && errors.hasExternalUsers.message) &&
                <InputErrorText message={errors.hasExternalUsers.message} data-testid={`${QA_ID_PREFIX}-external-users-error`} />
              }
            </div>
          }

          <div className='message-tracking-edit' data-testid={`${QA_ID_PREFIX}-tracking-container`}>
            <h3 data-testid={`${QA_ID_PREFIX}-tracking-title`}>Select your message tracking preference</h3>
            <p data-testid={`${QA_ID_PREFIX}-tracking-description`}>Discover how to track message performance to measure engagement and effectiveness.</p>
            
            <div data-testid={`${QA_ID_PREFIX}-tracking-options`}>
                <div className='switch-container' data-testid={`${QA_ID_PREFIX}-track-opens-container`}>
                  <div data-testid={`${QA_ID_PREFIX}-track-opens-label`}>
                    Track opens
                  </div>
                  <Controller
                    name="trackOpens"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        a11yLabelledBy="open"
                        name="trackOpens"
                        isOn={field.value}
                        onClick={() => {
                          if(isTemplateSaveRequired(formValues.channel, selectedCampaign, notificationIndex)) {
                            setNeedToSaveTemplate(true);
                          }
                          field.onChange({target: { value: !field.value}});
                          handleAutoSave(true);
                        }}
                        disabled={disableInputs}
                        data-testid={`${QA_ID_PREFIX}-track-opens-switch`}
                      />
                    )}
                  />
                </div>
                <div className='typo-1' style={{paddingTop: 0}} data-testid={`${QA_ID_PREFIX}-track-opens-description`}>Discover who opens your campaign and how many total opens the message received.</div>

                <div className='switch-container' style={{paddingTop: '12px'}} data-testid={`${QA_ID_PREFIX}-track-clicks-container`}>
                  <div data-testid={`${QA_ID_PREFIX}-track-clicks-label`}>
                    Track clicks
                  </div>
                  <Controller
                    name={"trackClicks"}
                    control={control}
                    render={({ field }) => (
                      <Switch
                        a11yLabelledBy="clicks"
                        name={"trackClicks"}
                        isOn={field.value}
                        onClick={() => {
                          if(isTemplateSaveRequired(formValues.channel, selectedCampaign, notificationIndex)) {
                            setNeedToSaveTemplate(true);
                          }
                          field.onChange({target: { value: !field.value}});
                          handleAutoSave(true);
                        }}
                        disabled={disableInputs}
                        data-testid={`${QA_ID_PREFIX}-track-clicks-switch`}
                      />
                    )}
                  />
                </div>
                <div className='typo-1' style={{paddingTop: 0}} data-testid={`${QA_ID_PREFIX}-track-clicks-description`}>Discover which links were clicked, how many times they were clicked and whi did the clicking.</div>

                <div className='switch-container' style={{paddingTop: '12px'}} data-testid={`${QA_ID_PREFIX}-advanced-tracking-container`}>
                  <div data-testid={`${QA_ID_PREFIX}-advanced-tracking-label`}>
                    Advanced tracking
                  </div>
                  <Controller
                    name={"advancedTracking"}
                    control={control}
                    render={({ field }) => (
                      <Switch
                        a11yLabelledBy="advancedTracking"
                        name={"advancedTracking"}
                        disabled={!formValues.trackOpens || disableInputs}
                        isOn={field.value}
                        onClick={() => {
                          field.onChange({target: { value: !field.value}});
                          handleAutoSave(true);
                        }}
                        data-testid={`${QA_ID_PREFIX}-advanced-tracking-switch`}
                      />
                    )}
                  />
                </div>
                <div className='typo-1' style={{paddingTop: 0}} data-testid={`${QA_ID_PREFIX}-advanced-tracking-description`}>Discover the Operating System, Device and Browser behind your message opens.</div>
            </div>
          </div>

          <EditFormActionsFooter
            hidePreviousButton={stepIndex === 0}
            loading={continueButtonLoading}
            onPrevious={() => handlePrevious()}
            onSaveDraft={() => saveDraft()}
            onContinue={() => handleContinue(stepIndex)}
            data-testid={`${QA_ID_PREFIX}-form-actions`}
          />
        </div>
      </div>
    </div>
  )
}

export default MessageDetailsEdit;